import {
  createRouter,
  createRootRoute,
  createRoute,
  redirect,
  Outlet,
} from '@tanstack/react-router';
import { z } from 'zod';
import { AppLayout } from './layouts/AppLayout';
import { DashboardPage } from './pages/Dashboard';
import { CampaignsPage } from './pages/Campaigns';
import { CampaignDetailPage } from './pages/CampaignDetail';
import { CreateCampaignPage } from './pages/CreateCampaign';
import { CampaignCatalogPage } from './pages/CampaignCatalog';
import { OpportunitiesPage } from './pages/Opportunities';
import { MarketIntelligencePage } from './pages/MarketIntelligence';
import { SettingsPage } from './pages/Settings';
import { OnboardingPage } from './pages/Onboarding';
import { LoginPage } from './pages/Login';
import { CatalogListPage } from './pages/CatalogList';
import { BalancePage } from './pages/Balance';
import { TopUpPage } from './pages/TopUp';
import { InvestmentProjectionPage } from './pages/InvestmentProjection';
import { SupportPage, SupportTicketPage } from './pages/Support';

const rootRoute = createRootRoute({
  component: Outlet,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const layoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: AppLayout,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/dashboard' });
  },
});

const dashboardRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/dashboard',
  component: DashboardPage,
});

const campaignsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/campaigns',
  component: CampaignsPage,
});

const campaignDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/campaigns/$campaignId',
  component: CampaignDetailPage,
});

const campaignCatalogRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/campaigns/$campaignId/catalog',
  component: CampaignCatalogPage,
});

const createCampaignSearchSchema = z.object({
  category: z.string().optional(),
  region: z.string().optional(),
  suggestedBudget: z.coerce.number().optional(),
});

const createCampaignRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/campaigns/new',
  component: CreateCampaignPage,
  validateSearch: (search) => createCampaignSearchSchema.parse(search),
});

const catalogRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/catalog',
  component: CatalogListPage,
});

const opportunitiesRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/opportunities',
  component: OpportunitiesPage,
});

const marketIntelligenceRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/market-intelligence',
  component: MarketIntelligencePage,
});

const supportRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/support',
  component: SupportPage,
});

const supportTicketRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/support/$ticketId',
  component: SupportTicketPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings',
  component: SettingsPage,
});

const balanceRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/balance',
  component: BalancePage,
});

const topUpRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/balance/top-up',
  component: TopUpPage,
});

const projectionRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/balance/projection',
  component: InvestmentProjectionPage,
});

const onboardingRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/onboarding',
  component: OnboardingPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  layoutRoute.addChildren([
    dashboardRoute,
    campaignsRoute,
    campaignDetailRoute,
    campaignCatalogRoute,
    createCampaignRoute,
    catalogRoute,
    opportunitiesRoute,
    marketIntelligenceRoute,
    balanceRoute,
    topUpRoute,
    projectionRoute,
    supportRoute,
    supportTicketRoute,
    settingsRoute,
    onboardingRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
